import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2,
  Database, Sparkles, HardDrive, Image as ImageIcon, Bot, Server, Power,
  Zap, Facebook, Instagram, ListChecks, Clock, ShieldAlert, CheckCircle,
  PauseCircle, HelpCircle,
} from "lucide-react";

const ICONS = {
  "Backend API": Server,
  "MongoDB": Database,
  "IA (Claude)": Sparkles,
  "Storage (S3)": HardDrive,
  "Media URLs": ImageIcon,
  "Automatización": Bot,
};

const SYS_COMPONENT_META = {
  portal:            { label: "Portal", Icon: Server },
  database:          { label: "Base de datos", Icon: Database },
  editorial_engine:  { label: "Motor editorial", Icon: Bot },
  scheduler:         { label: "Scheduler", Icon: Clock },
  scraping_engine:   { label: "Scraping", Icon: ListChecks },
  ai_provider:       { label: "IA", Icon: Sparkles },
  facebook:          { label: "Facebook", Icon: Facebook },
  instagram:         { label: "Instagram", Icon: Instagram },
  media_storage:     { label: "Almacenamiento", Icon: HardDrive },
  publication_queue: { label: "Cola de publicación", Icon: Zap },
};

const SYS_STATUS_META = {
  operational:  { label: "Operativo", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-300", Icon: CheckCircle2 },
  degraded:     { label: "Degradado", color: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-300",   Icon: AlertTriangle },
  critical:     { label: "Crítico",   color: "text-red-700",     bg: "bg-red-50",     border: "border-red-300",     Icon: XCircle },
  disconnected: { label: "Desconectado", color: "text-red-700",  bg: "bg-red-50",     border: "border-red-300",     Icon: XCircle },
  paused:       { label: "Pausado",   color: "text-slate-600",   bg: "bg-slate-100",  border: "border-slate-300",   Icon: PauseCircle },
  unknown:      { label: "Sin datos", color: "text-slate-600",   bg: "bg-slate-100",  border: "border-slate-300",   Icon: HelpCircle },
};

const STATUS_META = {
  ok:   { label: "Operativo", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-300", Icon: CheckCircle2 },
  warn: { label: "Advertencia", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", Icon: AlertTriangle },
  down: { label: "Caído", color: "text-red-700", bg: "bg-red-50", border: "border-red-300", Icon: XCircle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.warn;
  const { Icon } = m;
  return (
    <span data-testid={`health-status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${m.border} ${m.bg} ${m.color} text-[10px] uppercase tracking-widest font-bold`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export default function AdminHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [enablingAuto, setEnablingAuto] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.adminHealth();
      setData(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo obtener el estado del sistema");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  // Auto refresh every 60s
  useEffect(() => {
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  const handleEnableAutomation = async () => {
    if (enablingAuto) return;
    setEnablingAuto(true);
    try {
      await api.adminHealthEnableAutomation();
      toast.success("Motor de automatización activado");
      await load(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo activar el motor");
    } finally {
      setEnablingAuto(false);
    }
  };

  const overall = data?.overall || "warn";
  const overallMeta = STATUS_META[overall] || STATUS_META.warn;
  const OverallIcon = overallMeta.Icon;

  return (
    <div data-testid="admin-health-page" className="max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-nightblue" />
            <h1 className="font-serif text-2xl font-bold text-blueblack">Estado del sistema</h1>
          </div>
          <p className="text-sm text-textgray mt-1">
            Monitoreo en tiempo real de los servicios críticos. Se refresca automáticamente cada 60 segundos.
          </p>
        </div>
        <Button
          data-testid="health-refresh-btn"
          onClick={() => load(true)}
          disabled={loading || refreshing}
          variant="outline"
          className="h-9 rounded-none border-softblue text-nightblue text-[11px] uppercase tracking-widest font-semibold"
        >
          {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Reintentar
        </Button>
      </div>

      {loading && !data && (
        <div className="border border-softblue bg-white p-8 text-center text-textgray text-sm">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando estado…
        </div>
      )}

      <SystemHealthSection />

      {data && (
        <>
          <div data-testid="health-overall" className={`border ${overallMeta.border} ${overallMeta.bg} p-5 mb-6 flex items-center gap-3`}>
            <OverallIcon className={`w-8 h-8 ${overallMeta.color}`} />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-textgray">Estado general</div>
              <div className={`font-serif text-xl font-bold ${overallMeta.color}`}>
                {overall === "ok" && "Todos los sistemas operativos"}
                {overall === "warn" && "Sistemas con advertencias"}
                {overall === "down" && "Uno o más sistemas caídos"}
              </div>
            </div>
            <div className="text-right text-[10px] text-textgray">
              <div>Última verificación</div>
              <div className="font-mono text-blueblack">
                {new Date(data.generated_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.checks.map((c) => {
              const Icon = ICONS[c.name] || Server;
              const m = STATUS_META[c.status] || STATUS_META.warn;
              return (
                <div
                  key={c.name}
                  data-testid={`health-check-${c.name.replace(/\s+/g, "-").replace(/[()]/g, "").toLowerCase()}`}
                  className={`border ${m.border} bg-white p-4 flex items-start gap-3`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center ${m.bg} border ${m.border}`}>
                    <Icon className={`w-5 h-5 ${m.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-serif text-base font-bold text-blueblack truncate">{c.name}</div>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="text-xs text-textgray mt-1 break-words">{c.detail}</div>
                    {c.latency_ms != null && (
                      <div className="text-[10px] uppercase tracking-widest text-textgray mt-1.5 font-mono">
                        latencia: {c.latency_ms} ms
                      </div>
                    )}
                    {c.name === "Automatización" && c.code === "engine_disabled" && (
                      <Button
                        data-testid="health-enable-automation-btn"
                        onClick={handleEnableAutomation}
                        disabled={enablingAuto}
                        className="mt-3 h-8 rounded-none bg-nightblue hover:bg-blueblack text-white text-[10px] uppercase tracking-widest font-bold px-3"
                      >
                        {enablingAuto ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Power className="w-3.5 h-3.5 mr-1.5" />}
                        Activar motor
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 border border-softblue bg-lightgray/40 p-4 text-xs text-textgray">
            <div className="font-semibold text-blueblack mb-1">Interpretación de estados</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><b className="text-emerald-700">Operativo</b>: el servicio responde con latencia aceptable.</li>
              <li><b className="text-amber-700">Advertencia</b>: el servicio responde pero degradado o requiere atención.</li>
              <li><b className="text-red-700">Caído</b>: el servicio no está disponible y bloquea funciones que dependen de él.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}


function SysStatusBadge({ status }) {
  const m = SYS_STATUS_META[status] || SYS_STATUS_META.unknown;
  const { Icon } = m;
  return (
    <span data-testid={`sys-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${m.border} ${m.bg} ${m.color} text-[10px] uppercase tracking-widest font-bold`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function SeverityDot({ severity }) {
  const color = severity === "critical" ? "bg-red-500" : severity === "warning" ? "bg-amber-500" : "bg-slate-400";
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

function SystemHealthSection() {
  const [state, setState] = useState({ overall: "unknown", components: {}, generated_at: null });
  const [incidents, setIncidents] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testingAi, setTestingAi] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const [h, i, a] = await Promise.all([
        api.adminSystemHealth(false),
        api.adminSystemIncidents({ limit: 20 }),
        api.adminSystemActivity(20),
      ]);
      setState(h || { overall: "unknown", components: {}, generated_at: null });
      setIncidents(Array.isArray(i) ? i : []);
      setActivity(Array.isArray(a) ? a : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el estado del sistema");
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await api.adminSystemHealthRefresh();
      await load(true);
      toast.success("Estado actualizado");
    } catch { toast.error("No se pudo actualizar"); }
    finally { setRefreshing(false); }
  };

  const doAiTest = async () => {
    setTestingAi(true);
    try {
      const r = await api.adminSystemHealthAiTest();
      toast.success(`Prueba IA: ${r.status} · ${r.latency_ms ?? "?"}ms`);
    } catch { toast.error("Test IA falló"); }
    finally { setTestingAi(false); }
  };

  const doAck = async (id) => {
    try { await api.adminSystemIncidentAck(id); await load(true); toast.success("Reconocido"); }
    catch { toast.error("No se pudo reconocer"); }
  };
  const doResolve = async (id) => {
    try { await api.adminSystemIncidentResolve(id, "Resuelto manualmente"); await load(true); toast.success("Resuelto"); }
    catch { toast.error("No se pudo resolver"); }
  };

  if (loading) {
    return <div className="mb-6 border border-softblue bg-white p-8 text-center text-textgray text-sm">
      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando sistema…
    </div>;
  }

  const om = SYS_STATUS_META[state.overall] || SYS_STATUS_META.unknown;
  const OverallIcon = om.Icon;
  const openIncs = incidents.filter((i) => i.status !== "resolved");
  const resolvedIncs = incidents.filter((i) => i.status === "resolved").slice(0, 5);

  return (
    <div data-testid="system-health-section" className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-nightblue" />
          <h2 className="font-serif text-xl font-bold text-blueblack">Sistema · Etapa 6</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={doAiTest} disabled={testingAi} data-testid="sys-test-ai-btn"
            className="h-9 rounded-none border-softblue text-nightblue text-[11px] uppercase tracking-widest font-semibold">
            {testingAi ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            Probar IA
          </Button>
          <Button variant="outline" onClick={doRefresh} disabled={refreshing} data-testid="sys-refresh-btn"
            className="h-9 rounded-none border-softblue text-nightblue text-[11px] uppercase tracking-widest font-semibold">
            {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Verificar ahora
          </Button>
        </div>
      </div>

      <div data-testid="sys-overall" className={`border ${om.border} ${om.bg} p-5 mb-4 flex items-center gap-3`}>
        <OverallIcon className={`w-8 h-8 ${om.color}`} />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-textgray">Estado general del motor</div>
          <div className={`font-serif text-xl font-bold ${om.color}`}>{om.label}</div>
        </div>
        <div className="text-right text-[10px] text-textgray">
          <div>Última verificación</div>
          <div className="font-mono text-blueblack">
            {state.generated_at ? new Date(state.generated_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" }) : "–"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {Object.entries(SYS_COMPONENT_META).map(([key, meta]) => {
          const comp = state.components?.[key] || { status: "unknown", message: "–" };
          const m = SYS_STATUS_META[comp.status] || SYS_STATUS_META.unknown;
          const Icon = meta.Icon;
          return (
            <div key={key} data-testid={`sys-comp-${key}`} className={`border ${m.border} bg-white p-3 flex items-start gap-3`}>
              <div className={`w-9 h-9 flex items-center justify-center ${m.bg} border ${m.border}`}>
                <Icon className={`w-4 h-4 ${m.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-serif text-sm font-bold text-blueblack truncate">{meta.label}</div>
                  <SysStatusBadge status={comp.status} />
                </div>
                <div className="text-xs text-textgray mt-1 break-words">{comp.message || "–"}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-softblue bg-white">
          <div className="px-4 py-2 border-b border-softblue bg-lightgray/40 flex items-center justify-between">
            <div className="text-sm font-bold text-blueblack">Incidentes abiertos</div>
            <span className="text-[10px] uppercase tracking-widest text-textgray font-semibold">{openIncs.length}</span>
          </div>
          <div className="divide-y divide-softblue max-h-[300px] overflow-y-auto">
            {openIncs.length === 0 && (
              <div className="p-4 text-xs text-textgray flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" /> Sin incidentes abiertos.
              </div>
            )}
            {openIncs.map((i) => (
              <div key={i.id} data-testid={`sys-inc-${i.id}`} className="p-3 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <SeverityDot severity={i.severity} />
                  <div className="font-semibold text-blueblack">{i.title}</div>
                  <span className="ml-auto text-[10px] text-textgray uppercase">{i.status} · x{i.occurrence_count}</span>
                </div>
                <div className="text-textgray mb-2">{i.description || "–"}</div>
                <div className="text-[10px] text-textgray flex items-center justify-between">
                  <span>Componente: <b className="text-blueblack">{i.component}</b> · desde {i.started_at ? new Date(i.started_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" }) : "–"}</span>
                  <span className="flex gap-2">
                    {i.status !== "acknowledged" && (
                      <button onClick={() => doAck(i.id)} className="underline text-nightblue" data-testid={`sys-inc-ack-${i.id}`}>Reconocer</button>
                    )}
                    <button onClick={() => doResolve(i.id)} className="underline text-emerald-700" data-testid={`sys-inc-resolve-${i.id}`}>Resolver</button>
                  </span>
                </div>
              </div>
            ))}
          </div>
          {resolvedIncs.length > 0 && (
            <>
              <div className="px-4 py-2 border-t border-softblue bg-lightgray/40 text-[10px] uppercase tracking-widest text-textgray font-semibold">Resueltos recientes</div>
              <div className="divide-y divide-softblue">
                {resolvedIncs.map((i) => (
                  <div key={i.id} className="px-3 py-2 text-xs flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-emerald-600" />
                      <span className="text-blueblack">{i.title}</span>
                      <span className="text-textgray">({i.component})</span>
                    </div>
                    <span className="text-[10px] text-textgray">{i.resolved_at ? new Date(i.resolved_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" }) : ""}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="border border-softblue bg-white">
          <div className="px-4 py-2 border-b border-softblue bg-lightgray/40 text-sm font-bold text-blueblack">Actividad reciente</div>
          <div className="divide-y divide-softblue max-h-[420px] overflow-y-auto">
            {activity.length === 0 && (
              <div className="p-4 text-xs text-textgray">Sin actividad reciente.</div>
            )}
            {activity.map((a, idx) => (
              <div key={`${a.type}-${a.at}-${idx}`} className="p-3 text-xs">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-blueblack">
                    {a.type === "incident" ? a.title : `Corrida ${a.run_type || ""}`}
                  </div>
                  <span className="text-[10px] text-textgray">{a.at ? new Date(a.at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" }) : ""}</span>
                </div>
                <div className="text-textgray mt-0.5">
                  {a.type === "incident"
                    ? `${a.component} · ${a.severity} · ${a.status}${a.message ? " · " + a.message : ""}`
                    : `${a.status} · ${a.published} publicadas · ${a.urgents} urgentes${a.duration_ms ? " · " + Math.round(a.duration_ms/100)/10 + "s" : ""}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
